import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListSearchInput } from '@redpanda/ui';

interface FeedFilter {
  key: string | null;
  label: string;
  count?: number;
}

interface FeedToolbarProps {
  filters: FeedFilter[];
  active: string | null;
  onFilter: (key: string | null) => void;
  /** 搜尋：打完字 400ms 後才送出 */
  onSearch: (q: string) => void;
  searchPlaceholder: string;
  /** 搜尋框旁的主要按鈕（新增留言／許願） */
  action?: React.ReactNode;
}

/**
 * 留言、許願的工具列（兩頁同一排版）：篩選分段（附筆數）＋搜尋＋新增按鈕同一排；控制項與其他系統的列表工具列同高（h-10）
 * 篩選選項多時可以左右滑動（手機），不換行把版面撐高
 */
const FeedToolbar: React.FC<FeedToolbarProps> = ({ filters, active, onFilter, onSearch, searchPlaceholder, action }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  // 和上次送出的字一樣就不送（不用「是否首次」的 ref：開發模式 StrictMode 會重跑 effect，見 R16）
  const sent = useRef('');

  useEffect(() => {
    const q = search.trim();
    if (q === sent.current) return;
    const timer = setTimeout(() => { sent.current = q; onSearch(q); }, 400);
    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const segment = (selected: boolean) =>
    `inline-flex shrink-0 items-center rounded px-3 py-1.5 text-sm transition-colors ${selected ? 'bg-accent-soft text-accent font-medium' : 'text-fg-secondary hover:text-fg'}`;

  const filterNav = (
    <nav
      aria-label={t('pages.feedback.filter_label')}
      className="flex h-10 w-fit max-w-full items-center overflow-x-auto rounded-md border border-line bg-surface p-0.5"
      style={{ scrollbarWidth: 'none' }}
    >
      {filters.map(f => (
        <button key={String(f.key)} type="button" onClick={() => onFilter(f.key)} aria-pressed={active === f.key} className={segment(active === f.key)}>
          {f.label}
          {f.count !== undefined && <span className="ml-1.5 tabular-nums text-fg-muted">{f.count}</span>}
        </button>
      ))}
    </nav>
  );
  const searchBox = (
    <div className="flex items-center gap-2 sm:ml-auto">
      <div className="flex-1 sm:flex-none">
        <ListSearchInput value={search} onChange={setSearch} placeholder={searchPlaceholder} widthClass="sm:w-44" />
      </div>
      {action}
    </div>
  );

  return <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">{filterNav}{searchBox}</div>;
};

export default FeedToolbar;
